import { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "@/components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Globe, Loader2, Copy, CheckCircle2, AlertCircle, XCircle, Clock,
  ExternalLink, Trash2, Power, PowerOff, Shield, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft:         { label: "Keine Domain",     color: "bg-muted text-muted-foreground border-border",                   icon: Globe },
  dns_pending:   { label: "DNS prüfen",       color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30", icon: Clock },
  dns_verified:  { label: "Bereit zum Start", color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",  icon: CheckCircle2 },
  active:        { label: "Live",             color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  inactive:      { label: "Deaktiviert",      color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30", icon: PowerOff },
};

// Cloudflare SSL-Status-Mapping (ssl.status Feld aus der CF-Custom-Hostname-API).
// Terminalzustände: active (OK) / pending_deletion / deleted. Alles andere =
// laufende Validierung oder Zertifikat-Ausstellung.
const CF_SSL_STATUS_META = {
  active:               { label: "SSL aktiv",      color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", icon: Shield },
  pending_validation:   { label: "DCV ausstehend",  color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30", icon: Clock },
  pending_issuance:     { label: "Wird ausgestellt", color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30", icon: Loader2 },
  pending_deployment:   { label: "Wird deployed",    color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30", icon: Loader2 },
  pending_expiration:   { label: "Bald ablaufend",   color: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30", icon: AlertCircle },
  expired:              { label: "Abgelaufen",       color: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30", icon: XCircle },
  deleted:              { label: "Entfernt",         color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30", icon: Trash2 },
  pending_deletion:     { label: "Wird entfernt",    color: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30", icon: Trash2 },
  initializing:         { label: "Initialisiert",    color: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30", icon: Loader2 },
  blocked:              { label: "Blockiert",        color: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30", icon: XCircle },
};

const DNS_ERROR_HINT = {
  dns_not_found:        "DNS-Eintrag nicht gefunden — CNAME wurde noch nicht gesetzt oder propagiert noch.",
  cname_target_mismatch: "CNAME zeigt nicht auf unser Ziel — bitte Eintrag korrigieren.",
  apex_without_cname:   "Apex-Domain (ohne www.) unterstützt kein CNAME. Verwende stattdessen einen ALIAS/ANAME-Record oder nutze die www-Subdomain.",
  invalid_domain:       "Die Domain ist ungültig.",
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.color}`}>
      <Icon size={12} />{meta.label}
    </span>
  );
}

function CloudflareSslBadge({ cloudflare }) {
  const cf = cloudflare || {};
  if (!cf.cf_hostname_id) {
    return (
      <span data-testid="cf-ssl-badge-none" className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-muted text-muted-foreground border-border">
        <Shield size={11}/>Nicht registriert
      </span>
    );
  }
  const status = cf.ssl_status || "initializing";
  const meta = CF_SSL_STATUS_META[status] || { label: status, color: "bg-muted text-muted-foreground border-border", icon: Shield };
  const Icon = meta.icon;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid={`cf-ssl-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.color} cursor-help`}>
            <Icon size={11} className={status.startsWith("pending_") || status === "initializing" ? "animate-spin" : ""}/>
            {meta.label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <div className="text-xs space-y-1">
            <div><b>CF Hostname-ID:</b> <span className="font-mono">{cf.cf_hostname_id}</span></div>
            {cf.ssl_method && <div><b>Validierungs-Methode:</b> {cf.ssl_method}</div>}
            {cf.last_sync_at && <div><b>Letzter Sync:</b> {new Date(cf.last_sync_at).toLocaleString("de-DE")}</div>}
            {Array.isArray(cf.ssl_validation_errors) && cf.ssl_validation_errors.length > 0 && (
              <div className="mt-1 text-red-500">
                <b>Fehler:</b>
                <ul className="list-disc ml-4">
                  {cf.ssl_validation_errors.slice(0, 3).map((e, i) => <li key={i}>{typeof e === "string" ? e : (e.message || JSON.stringify(e))}</li>)}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CopyCnameValue({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid="copy-cname-btn"
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { toast.error("Konnte nicht kopieren"); }
      }}
      className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/50 hover:bg-muted px-2.5 py-1.5 text-xs font-mono transition-colors"
    >
      <span className="text-foreground">{value}</span>
      {copied
        ? <CheckCircle2 size={14} className="text-emerald-500" />
        : <Copy size={14} className="text-muted-foreground" />
      }
    </button>
  );
}

function DomainSetupDialog({ open, onOpenChange, project, cnameTarget, onSaved }) {
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && project) setDomain(project.custom_domain || "");
  }, [open, project]);

  const save = useCallback(async () => {
    if (!project) return;
    setBusy(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/live-sites/${project.project_id}/domain`,
        { custom_domain: domain },
      );
      toast.success(domain ? "Domain gespeichert — jetzt DNS prüfen" : "Domain entfernt");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  }, [domain, project, onOpenChange, onSaved]);

  if (!project) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="domain-setup-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe size={18} />Custom-Domain verknüpfen</DialogTitle>
          <DialogDescription>
            Hinterlege die Domain, unter der die Demo für <b>{project.company_name || project.public_slug}</b> später erreichbar sein soll.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="block text-xs font-medium mb-1.5 text-muted-foreground">Kunden-Domain</label>
            <Input
              data-testid="domain-input"
              placeholder="z.B. malerbetrieb-mueck.de"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              autoFocus
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Ohne Protokoll. Subdomain (www.) wird automatisch auf die Root-Domain normalisiert.
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Shield size={14} />DNS-Anleitung für den Kunden
            </div>
            <p className="text-sm text-foreground">
              Der Kunde setzt bei seinem Domain-Provider folgenden CNAME-Eintrag:
            </p>
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm font-mono">
              <span className="text-muted-foreground">Typ</span><span>CNAME</span>
              <span className="text-muted-foreground">Name</span><span className="flex items-center gap-1"><code className="text-xs">{domain || "(deine Domain)"}</code><span className="text-[10px] text-muted-foreground">oder www</span></span>
              <span className="text-muted-foreground">Wert</span><CopyCnameValue value={cnameTarget || "live.signalsolution.de"} />
              <span className="text-muted-foreground">TTL</span><span>Auto / 300s</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {project.custom_domain && (
            <Button
              data-testid="remove-domain-btn"
              variant="ghost"
              className="text-destructive hover:text-destructive mr-auto"
              disabled={busy}
              onClick={async () => { setDomain(""); setTimeout(save, 0); }}
            ><Trash2 size={14} className="mr-1.5" />Entfernen</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button data-testid="save-domain-btn" onClick={save} disabled={busy}>
            {busy ? <Loader2 className="animate-spin" size={14}/> : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DnsDetailCell({ site }) {
  const v = site.dns?.verify;
  if (!v || !v.checked_at) {
    return <span className="text-xs text-muted-foreground">noch nicht geprüft</span>;
  }
  if (v.ok) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
        <CheckCircle2 size={12}/>CNAME korrekt
      </span>
    );
  }
  const hint = DNS_ERROR_HINT[v.error] || v.error || "DNS-Prüfung fehlgeschlagen";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 cursor-help">
            <AlertCircle size={12}/>{v.error === "dns_not_found" ? "DNS fehlt" : "Fehler"}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <p>{hint}</p>
            {v.cname_chain?.length > 0 && (
              <p className="font-mono opacity-70">Gefunden: {v.cname_chain.join(" → ")}</p>
            )}
            {v.a_records?.length > 0 && (
              <p className="font-mono opacity-70">A-Records: {v.a_records.join(", ")}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function LiveSitesPage() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [cnameTarget, setCnameTarget] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [filter, setFilter] = useState("");
  const [verifying, setVerifying] = useState(new Set());
  const [toggling, setToggling] = useState(new Set());
  const [cfWorking, setCfWorking] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/live-sites`);
      setItems(data.items || []);
      setCnameTarget(data.cname_target || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verifyDns = useCallback(async (site) => {
    setVerifying(prev => new Set([...prev, site.project_id]));
    try {
      const { data } = await axios.post(`${API}/admin/live-sites/${site.project_id}/verify-dns`);
      if (data.dns?.ok) toast.success(`DNS für ${site.custom_domain} ist korrekt konfiguriert`);
      else toast.warning(DNS_ERROR_HINT[data.dns?.error] || "DNS noch nicht fertig propagiert");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DNS-Prüfung fehlgeschlagen");
    } finally {
      setVerifying(prev => { const next = new Set(prev); next.delete(site.project_id); return next; });
    }
  }, [load]);

  const toggleLive = useCallback(async (site) => {
    const target = site.status === "active" ? "inactive" : "active";
    setToggling(prev => new Set([...prev, site.project_id]));
    try {
      await axios.post(`${API}/admin/live-sites/${site.project_id}/status`, { status: target });
      toast.success(target === "active" ? "Seite ist live" : "Seite deaktiviert");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Änderung fehlgeschlagen");
    } finally {
      setToggling(prev => { const next = new Set(prev); next.delete(site.project_id); return next; });
    }
  }, [load]);

  const cloudflareAction = useCallback(async (site, action) => {
    setCfWorking(prev => new Set([...prev, site.project_id]));
    try {
      if (action === "register") {
        const { data } = await axios.post(`${API}/admin/live-sites/${site.project_id}/cloudflare-register`);
        toast.success(`Cloudflare: ${site.custom_domain} registriert${data.ssl_status === "active" ? " (SSL aktiv)" : ""}`);
      } else if (action === "sync") {
        const { data } = await axios.post(`${API}/admin/live-sites/${site.project_id}/cloudflare-sync`);
        toast.success(`SSL-Status: ${data.ssl_status || "unbekannt"}`);
      } else if (action === "unregister") {
        await axios.delete(`${API}/admin/live-sites/${site.project_id}/cloudflare`);
        toast.success("Aus Cloudflare entfernt");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cloudflare-Aktion fehlgeschlagen");
    } finally {
      setCfWorking(prev => { const next = new Set(prev); next.delete(site.project_id); return next; });
    }
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(s =>
      (s.company_name || "").toLowerCase().includes(q) ||
      (s.public_slug || "").toLowerCase().includes(q) ||
      (s.custom_domain || "").toLowerCase().includes(q)
    );
  }, [items, filter]);

  const stats = useMemo(() => {
    const counts = { total: items.length, active: 0, pending: 0, ready: 0 };
    items.forEach(s => {
      if (s.status === "active") counts.active++;
      else if (s.status === "dns_pending") counts.pending++;
      else if (s.status === "dns_verified") counts.ready++;
    });
    return counts;
  }, [items]);

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="live-sites-page">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Globe className="text-primary" size={22}/>Live Sites
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Verknüpfe Kunden-Domains mit Demo-Projekten. Status und DNS werden hier live geprüft.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
              <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`}/>Aktualisieren
            </Button>
          </div>
        </header>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Projekte", value: stats.total, color: "text-foreground" },
            { label: "Live aktiv", value: stats.active, color: "text-emerald-600 dark:text-emerald-400" },
            { label: "Bereit zum Start", value: stats.ready, color: "text-sky-600 dark:text-sky-400" },
            { label: "DNS ausstehend", value: stats.pending, color: "text-amber-600 dark:text-amber-400" },
          ].map(s => (
            <div key={s.label} className="rounded-lg border border-border bg-card p-4">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* CNAME-Hinweis */}
        <div className="rounded-lg border border-border bg-primary/5 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <span className="font-medium">CNAME-Ziel für Kunden: </span>
            <span className="text-muted-foreground">Wird in der Domain-Anleitung angezeigt</span>
          </div>
          <CopyCnameValue value={cnameTarget || "live.signalslt.de"} />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Input
            data-testid="live-sites-filter"
            placeholder="Suchen: Firma, Slug oder Domain…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-md"
          />
          <Badge variant="secondary" className="font-normal">{filtered.length} / {items.length}</Badge>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-border overflow-hidden bg-card">
          <table className="w-full text-sm" data-testid="live-sites-table">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Projekt</th>
                <th className="text-left font-medium px-4 py-2.5">Custom-Domain</th>
                <th className="text-left font-medium px-4 py-2.5">Status</th>
                <th className="text-left font-medium px-4 py-2.5">DNS</th>
                <th className="text-left font-medium px-4 py-2.5">SSL / Cloudflare</th>
                <th className="text-right font-medium px-4 py-2.5">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  <Loader2 className="animate-spin inline mr-2" size={14}/>Lade…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  Keine Projekte gefunden.
                </td></tr>
              )}
              {!loading && filtered.map(site => (
                <tr key={site.project_id} data-testid={`live-site-row-${site.project_id}`} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-foreground">{site.company_name || "(unbenannt)"}</div>
                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{site.public_slug}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {site.custom_domain ? (
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs">{site.custom_domain}</span>
                        {site.status === "active" && (
                          <a
                            href={`https://${site.custom_domain}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-muted-foreground hover:text-primary transition-colors"
                            data-testid="live-site-open-link"
                          ><ExternalLink size={12}/></a>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">keine Domain</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top"><StatusBadge status={site.status}/></td>
                  <td className="px-4 py-3 align-top"><DnsDetailCell site={site}/></td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-col items-start gap-1">
                      <CloudflareSslBadge cloudflare={site.cloudflare}/>
                      {site.cloudflare?.cf_hostname_id && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm" variant="ghost"
                            className="h-6 px-1.5 text-[10px]"
                            data-testid={`cf-sync-btn-${site.project_id}`}
                            disabled={cfWorking.has(site.project_id)}
                            onClick={() => cloudflareAction(site, "sync")}
                          >
                            {cfWorking.has(site.project_id)
                              ? <Loader2 size={10} className="animate-spin"/>
                              : <><RefreshCw size={10} className="mr-1"/>Sync</>
                            }
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-6 px-1.5 text-[10px] text-destructive hover:text-destructive"
                            data-testid={`cf-unregister-btn-${site.project_id}`}
                            disabled={cfWorking.has(site.project_id)}
                            onClick={() => { if (window.confirm(`Hostname ${site.custom_domain} wirklich aus Cloudflare entfernen? Das revoziert auch das SSL-Zertifikat.`)) cloudflareAction(site, "unregister"); }}
                          >
                            <Trash2 size={10}/>
                          </Button>
                        </div>
                      )}
                      {!site.cloudflare?.cf_hostname_id && site.custom_domain && site.dns?.verify?.ok && (
                        <Button
                          size="sm" variant="outline"
                          className="h-6 px-2 text-[10px]"
                          data-testid={`cf-register-btn-${site.project_id}`}
                          disabled={cfWorking.has(site.project_id)}
                          onClick={() => cloudflareAction(site, "register")}
                        >
                          {cfWorking.has(site.project_id)
                            ? <Loader2 size={10} className="animate-spin"/>
                            : <><Shield size={10} className="mr-1"/>CF registrieren</>
                          }
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {site.custom_domain && (
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`verify-dns-btn-${site.project_id}`}
                          disabled={verifying.has(site.project_id)}
                          onClick={() => verifyDns(site)}
                        >
                          {verifying.has(site.project_id)
                            ? <Loader2 size={13} className="animate-spin"/>
                            : <><RefreshCw size={13} className="mr-1"/>DNS prüfen</>
                          }
                        </Button>
                      )}
                      {site.status === "dns_verified" && (
                        <Button
                          size="sm"
                          data-testid={`go-live-btn-${site.project_id}`}
                          disabled={toggling.has(site.project_id)}
                          onClick={() => toggleLive(site)}
                        >
                          {toggling.has(site.project_id)
                            ? <Loader2 size={13} className="animate-spin"/>
                            : <><Power size={13} className="mr-1"/>Live schalten</>
                          }
                        </Button>
                      )}
                      {site.status === "active" && (
                        <Button
                          size="sm" variant="outline"
                          data-testid={`deactivate-btn-${site.project_id}`}
                          disabled={toggling.has(site.project_id)}
                          onClick={() => toggleLive(site)}
                        >
                          {toggling.has(site.project_id)
                            ? <Loader2 size={13} className="animate-spin"/>
                            : <><PowerOff size={13} className="mr-1"/>Deaktivieren</>
                          }
                        </Button>
                      )}
                      {site.status === "inactive" && (
                        <Button
                          size="sm" variant="outline"
                          data-testid={`reactivate-btn-${site.project_id}`}
                          disabled={toggling.has(site.project_id)}
                          onClick={() => toggleLive(site)}
                        ><Power size={13} className="mr-1"/>Reaktivieren</Button>
                      )}
                      <Button
                        size="sm" variant="outline"
                        data-testid={`configure-domain-btn-${site.project_id}`}
                        onClick={() => { setEditingProject(site); setDialogOpen(true); }}
                      >
                        <Globe size={13} className="mr-1"/>
                        {site.custom_domain ? "Bearbeiten" : "Domain"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <DomainSetupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={editingProject}
        cnameTarget={cnameTarget}
        onSaved={load}
      />
    </AppLayout>
  );
}
